import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import CTAButton from '../common/CTAButton';
import ResponsiveHeroImage from '../common/ResponsiveHeroImage';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

interface HeroSlide {
  id: string;
  title: string;
  subtitle: string;
  description: string;
  ctaText: string;
  ctaLink: string;
  backgroundImage: string;
  serviceType: 'investments' | 'insurance' | 'planning';
}

interface SlideWithMobileSettings extends HeroSlide {
  mobilePosition?: string;
  desktopPosition?: string;
  mobileFocalPoint?: 'top' | 'center' | 'bottom' | 'custom';
}

const slides: SlideWithMobileSettings[] = [
  {
    id: 'investments',
    title: 'Strategic Investment Solutions for Growth',
    subtitle: '',
    description: 'Tax-efficient investment strategies designed specifically for business owners and agricultural producers. Maximize returns while minimizing your tax burden.',
    ctaText: 'Explore Investments',
    ctaLink: '/services#savings-retirement-section',
    backgroundImage: '/assets/mountains-2.png',
    serviceType: 'investments',
    // Mobile: Focus on mountain peaks
    mobileFocalPoint: 'top',
    desktopPosition: 'center 30%'
  },
  {
    id: 'main',
    title: 'Financial Planning That Works As Hard As You Do',
    subtitle: '',
    description: 'Trusted financial guidance for the people driving SK & AB\'s farms and businesses. We combine practical strategies with personalized service to help secure what you\'ve built and where you\'re headed.',
    ctaText: 'Get Started',
    ctaLink: '/contact',
    backgroundImage: '/assets/Harvester1.JPG',
    serviceType: 'planning',
    // Mobile: Focus on harvester
    mobileFocalPoint: 'center',
    mobilePosition: 'center 40%',
    desktopPosition: 'center 30%'
  },
  {
    id: 'insurance',
    title: 'Insurance That Protects & Creates Wealth',
    subtitle: '',
    description: 'More than just coverage - strategic insurance planning that protects your assets, reduces taxes, and builds generational wealth.',
    ctaText: 'Find Your Coverage',
    ctaLink: '/services#insurance-section',
    backgroundImage: '/assets/mountain-curve.png',
    serviceType: 'insurance',
    // Mobile: Lower and slightly right to show the dramatic curve better
    mobileFocalPoint: 'custom',
    mobilePosition: '60% 70%',  // Right 60%, Down 70%
    desktopPosition: 'center center'
  }
];

export default function RotatingHero() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [loadedImages, setLoadedImages] = useState<Set<number>>(new Set([0])); // Pre-load first slide
  const [hasStarted, setHasStarted] = useState(false);
  const [imagesReady, setImagesReady] = useState<boolean[]>(new Array(slides.length).fill(false));
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // Start animation immediately on mount
  useEffect(() => {
    setHasStarted(true);
  }, []);

  // Auto-rotate slides
  useEffect(() => {
    if (!isPaused) {
      intervalRef.current = setInterval(() => {
        handleNextSlide();
      }, 8000); // 8 seconds per slide for smoother experience
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [currentSlide, isPaused]);

  // Mark when images are ready
  const handleImageReady = (index: number) => {
    setImagesReady(prev => {
      const next = [...prev];
      next[index] = true;
      return next;
    });
    setLoadedImages((prev) => new Set(prev).add(index));
  };

  const handleNextSlide = () => {
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
      setIsTransitioning(false);
    }, 400);
  };

  const handlePrevSlide = () => {
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
      setIsTransitioning(false);
    }, 400);
  };

  const handleDotClick = (index: number) => {
    if (index !== currentSlide) {
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentSlide(index);
        setIsTransitioning(false);
      }, 400);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowLeft') {
      handlePrevSlide();
    } else if (e.key === 'ArrowRight') {
      handleNextSlide();
    }
  };

  const slide = slides[currentSlide];

  return (
    <div
      className="w-full flex-grow flex items-center justify-center relative min-h-[90vh] md:min-h-screen bg-gradient-to-b from-premium-darker to-premium-dark overflow-hidden"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      onKeyDown={handleKeyDown}
      role="region"
      aria-label="Hero carousel"
      tabIndex={0}
    >
      {/* Background Images Container with Ken Burns Effect */}
      <div className="absolute inset-0">
        {slides.map((s, index) => {
          return (
            <div
              key={s.id}
              className={`absolute inset-0 transition-opacity duration-[1500ms] ease-in-out ${
                index === currentSlide ? 'opacity-75' : 'opacity-0'
              }`}
            >
              <div
                className={`absolute inset-0 ${
                  index === currentSlide 
                    ? hasStarted 
                      ? 'animate-kenburns' 
                      : 'scale-105'
                    : 'scale-100'
                }`}
                style={{
                  transformOrigin: index % 2 === 0 ? 'center right' : 'center left',
                }}
              >
                {/* Use responsive hero image with mobile optimization */}
                <ResponsiveHeroImage
                  src={s.backgroundImage}
                  alt={`${s.title} background`}
                  className="absolute inset-0 w-full h-full"
                  loading={index === 0 ? 'eager' : 'lazy'}
                  priority={index === 0}
                  onLoad={() => handleImageReady(index)}
                  mobileFocalPoint={s.mobileFocalPoint}
                  mobilePosition={s.mobilePosition}
                  desktopPosition={s.desktopPosition}
                />
              </div>
            </div>
          );
        })}
        {/* Subtle gradient overlay for text readability */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-black/20" />
      </div>

      {/* Centered Content Container - Mobile Optimized */}
      <div className="container max-w-3xl mx-auto px-4 py-16 sm:py-20 md:py-24 relative z-10 text-center hero-content-container">
        {/* Single Column Centered Content */}
        <div className="max-w-2xl mx-auto">
          <div
            className={`transition-all duration-[800ms] ease-in-out ${
              isTransitioning 
                ? 'opacity-0 transform translate-y-4' 
                : 'opacity-100 transform translate-y-0'
            }`}
          >
            {/* Mobile-optimized heading sizes */}
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 sm:mb-6 text-white leading-tight max-w-3xl mx-auto">
              {slide.title.split(' ').map((word, i) => {
                // Add line break for better formatting if needed
                if (slide.id === 'main' && i === 2) {
                  return <span key={i}>{word}<br className="hidden md:block" /> </span>;
                }
                return <span key={i}>{word} </span>;
              })}
            </h1>
            <div className="w-16 sm:w-20 h-1 bg-amber-400 mb-6 sm:mb-8 mx-auto"></div>
            {/* Mobile-optimized description */}
            <p className="text-sm sm:text-base md:text-lg text-white mb-8 sm:mb-10 leading-relaxed max-w-xl sm:max-w-2xl mx-auto font-medium drop-shadow-lg px-4 sm:px-0">
              {slide.description}
            </p>

            {/* Dual CTA Buttons */}
            <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-5">
              <CTAButton to={slide.ctaLink} variant="primary">
                <span>{slide.ctaText}</span>
              </CTAButton>
              <CTAButton to="/services" variant="secondary">
                <span>Services</span>
              </CTAButton>
            </div>
          </div>
        </div>
      </div>

      {/* Slide Navigation - Elegant Dots */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-30">
        <div className="flex gap-3 items-center">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => handleDotClick(index)}
              className={`transition-all duration-[400ms] ease-out ${
                index === currentSlide
                  ? 'w-8 h-2 bg-white'
                  : 'w-2 h-2 bg-white/40 hover:bg-white/60'
              } rounded-full`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </div>

      {/* Arrow Controls - More Subtle */}
      <button
        onClick={handlePrevSlide}
        className="absolute left-4 top-1/2 transform -translate-y-1/2 p-3 rounded-full bg-white/10 backdrop-blur-sm text-white hover:bg-white/20 transition-all duration-300 hidden md:block"
        aria-label="Previous slide"
      >
        <ChevronLeftIcon className="w-6 h-6" />
      </button>
      <button
        onClick={handleNextSlide}
        className="absolute right-4 top-1/2 transform -translate-y-1/2 p-3 rounded-full bg-white/10 backdrop-blur-sm text-white hover:bg-white/20 transition-all duration-300 hidden md:block"
        aria-label="Next slide"
      >
        <ChevronRightIcon className="w-6 h-6" />
      </button>
    </div>
  );
}